import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { LogType, RequestStatus } from '@gokrew/models/timesheet';
import { formatDate, formatTime } from './utility';

dayjs.extend(customParseFormat);
import { Tone } from '@gokrew/models/ui.types';
import type {
  RawTimesheetRequest,
  RawRequestSegment,
  TimesheetRequest,
  TimeLogEntry,
} from '@/types/timesheet';

export const getReqStatusLabel = (
  t: (key: string) => string
): Record<RequestStatus, string> => ({
  [RequestStatus.Pending]: t('timesheet.requestStatus.pending'),
  [RequestStatus.Approved]: t('timesheet.requestStatus.approved'),
  [RequestStatus.Rejected]: t('timesheet.requestStatus.rejected'),
});

export const getRequestStatusColor: Record<RequestStatus, Tone> = {
  [RequestStatus.Pending]: Tone.WARNING,
  [RequestStatus.Approved]: Tone.SUCCESS,
  [RequestStatus.Rejected]: Tone.ERROR,
};

const resolveSegTime = (iso?: string, hhmm?: string | null, locale = 'en'): string => {
  if (hhmm) return dayjs(hhmm, 'HH:mm').format('h:mm A');
  if (iso) return formatTime(iso, locale);
  return '--';
};

export const mapToTimesheetRequest = (
  req: RawTimesheetRequest,
  locale = 'en'
): TimesheetRequest => {
  const segments = req.proposed?.resultSegments ;
  return {
    id: req.id,
    date: formatDate(req.workDate, { format: 'D MMMM YYYY', locale }),
    submittedAt: formatDate(req.createdAt, {
      format: 'D MMMM YYYY hh:mm A',
      locale,
    }),
    employee: req.employee,
    status: req.status.toLowerCase(),
    reason: req.reason ?? undefined,
    isStale: req.isStale,
    decisionNote: req.decisionNote ?? undefined,
    timeLogs: segments?.map(
      (seg: RawRequestSegment): TimeLogEntry => ({
        in: resolveSegTime(seg.startTime, seg.startTime, locale),
        out: resolveSegTime(seg.endedAt ?? undefined, seg.endTime, locale),
        type: seg.type.toLowerCase() as 'clock' | 'break',
        note: seg.note ?? seg.employeeNote ?? undefined,
      })
    ),
  };
};

const parseTime = (time: string): dayjs.Dayjs | null => {
  if (!time || time === '--' || time === '--:--') return null;
  // backend's '24:00' midnight-end sentinel === 00:00; the negative-diff
  // wraparound below already handles it as end-of-day
  const normalized = time.startsWith('24:') ? time.replace('24:', '00:') : time;
  const fmt = /AM|PM/i.test(normalized) ? 'hh:mm A' : 'HH:mm';
  const d = dayjs(normalized, fmt, true);
  return d.isValid() ? d : null;
};

export interface FormTimeLog {
  segmentId: string;
  in: string;
  out: string;
  type: LogType;
  employeeNote: string;
  managerNote: string;
  isOvernightSplit?: boolean;
}

export interface SegmentLike {
  id: string;
  startTime: string;
  endTime: string | null;
  type: string;
  employeeNote: string | null;
  hrNote: string | null;
  isOvernightSplit?: boolean;
}

export const mapSegmentToFormLog = (s: SegmentLike): FormTimeLog => ({
  segmentId: s.id,
  in: s.startTime.slice(0, 5),
  out: s.endTime ? s.endTime.slice(0, 5) : '',
  type: s.type === 'BREAK' ? LogType.Break : LogType.Clock,
  employeeNote: s.employeeNote ?? '',
  managerNote: s.hrNote ?? '',
  isOvernightSplit: s.isOvernightSplit,
});

export const calcDuration = (inTime: string, outTime: string): string => {
  const start = parseTime(inTime);
  const end = parseTime(outTime);
  if (!start || !end) return '—';
  const rawTotal = end.diff(start, 'minute');
  const total = rawTotal < 0 ? rawTotal + 24 * 60 : rawTotal;
  if (total <= 0) return '—';
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};
